import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import compose from 'recompose/compose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ThumbsUp from 'components/ThumbsUp'
import {
  VOTE_DIRECTION_UP,
  VOTE_DIRECTION_DOWN,
  TOTAL_VOTES_THRESHOLD,
} from 'services/recommend'
import { CONTENT_RATING_GA4_EVENT } from 'services/event-tracking'
import { Map } from 'immutable'

export const SIZE_SMALL = 'x-small'
export const SIZE_MEDIUM = 'small'
export const SIZE_LARGE = 'large'

function getThumbsClass (state, prop) {
  const className = ['vote__thumbs-up']
  if (state[prop]) {
    className.push('vote__thumbs-up--active')
  }

  return [className.join(' ')]
}

function getThumbsUpClass (state) {
  return getThumbsClass(state, 'vote')
}

function getThumbsDownClass (state) {
  return getThumbsClass(state, 'voteDown')
}

function getVoteClass (props) {
  const { size } = props
  return [`vote__value thumbs-up__value--${size} vote__value--${size}`]
}

function getIconClass (props, extra) {
  const { size } = props
  const className = [
    'vote__icon',
    `thumbs-up__icon--${size}`,
    `vote__icon--${size}`,
  ]

  if (extra) {
    className.push(extra)
  }

  return [className.join(' ')]
}

function getTotalVote (props, state) {
  const { voteDown, vote } = props
  const { vote: voteState, voteDown: voteDownState } = state
  return voteDown + voteDownState + vote + voteState
}

function getPercent (num, total) {
  if (total === 0) {
    return total
  }

  const percent = num / total

  return Math.round(percent * 100)
}

function getCanVote (voteProp, auth) {
  return auth.has('jwt') && voteProp <= 0
}

class Vote extends Component {
  constructor (props) {
    super(props)
    this.state = {
      vote: 0,
      voteDown: 0,
    }
  }

  componentDidMount () {
    const { props } = this
    const { userVote } = props
    if (userVote) {
      this.setState(() => ({
        voteDown: userVote === 'DOWN' ? 1 : 0,
        vote: userVote === 'UP' ? 1 : 0,
      }))
    }
  }

  addVoteDown = () => {
    const { setDefaultGa4Event, ga4EventData } = this.props

    this.setState(() => ({
      voteDown: 1,
      vote: 0,
    }))

    // GA4 EVENT
    if (ga4EventData.size > 0) {
      setDefaultGa4Event(CONTENT_RATING_GA4_EVENT
        .mergeDeep(ga4EventData)
        .mergeDeep({ content_rating: 'down' }))
    }
  }

  addVoteUp = () => {
    const { upOnly, setDefaultGa4Event, ga4EventData } = this.props

    if (upOnly) {
      return
    }

    this.setState(() => ({
      vote: 1,
      voteDown: 0,
    }))

    // GA4 EVENT
    if (ga4EventData.size > 0) {
      setDefaultGa4Event(CONTENT_RATING_GA4_EVENT
        .mergeDeep(ga4EventData)
        .mergeDeep({ content_rating: 'up' }))
    }
  }

  render () {
    const { props, state } = this
    const {
      vote = 0,
      voteDown = 0,
      auth,
      className,
      voteId,
      upOnly,
      hideResult,
    } = props
    const { vote: voteState, voteDown: voteDownState } = state
    const totalVote = getTotalVote(props, state)
    const upPercent = getPercent(vote + voteState, totalVote)
    const downPercent = getPercent(voteDown + voteDownState, totalVote)
    const canVoteUp = getCanVote(voteState, auth)
    const canVoteDown = getCanVote(voteDownState, auth)
    const isUnderThreshold = vote + voteDown < TOTAL_VOTES_THRESHOLD
    const shouldHideResult = hideResult || isUnderThreshold

    return (
      <div className={`vote ${className}`}>
        {
          upOnly && isUnderThreshold
            ? null
            : <ThumbsUp
              hideResult={shouldHideResult}
              onVote={this.addVoteUp}
              auth={auth}
              vote={upPercent}
              units="%"
              canVote={upOnly ? false : canVoteUp}
              voteId={voteId}
              thumbsUpClass={getThumbsUpClass(state)}
              iconClass={getIconClass(props)}
              score={VOTE_DIRECTION_UP}
              voteClass={getVoteClass(props)}
            />
        }
        {
          upOnly
            ? null
            : <ThumbsUp
              hideResult={shouldHideResult}
              auth={auth}
              vote={downPercent}
              onVote={this.addVoteDown}
              canVote={canVoteDown}
              thumbsUpClass={getThumbsDownClass(state)}
              units="%"
              voteId={voteId}
              score={VOTE_DIRECTION_DOWN}
              iconClass={getIconClass(props, 'vote__icon--down')}
              voteClass={getVoteClass(props)}
            />
        }
      </div>
    )
  }
}

Vote.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  size: PropTypes.string,
  className: PropTypes.string,
  vote: PropTypes.number,
  voteDown: PropTypes.number,
  upOnly: PropTypes.bool,
  voteId: PropTypes.number.isRequired,
  hideResult: PropTypes.bool,
  ga4EventData: ImmutablePropTypes.map,
  userVote: PropTypes.string,
}

Vote.defaultProps = {
  size: SIZE_MEDIUM,
  vote: 0,
  voteDown: 0,
  ga4EventData: Map(),
}

export default compose(
  connectRedux(
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
      }
    },
  ),
)(Vote)
